import React, { useState, useContext, useCallback, useRef } from 'react';
import {
    Autocomplete,
    TextField,
    InputAdornment,
    Box,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import { useFlatIndicators } from '../utils/indicatorsrList';
import { FiltersContext } from "../store/filters-options-context";
import { useNavigate } from "react-router-dom";
import { fuzzySearchRanked } from '../utils/fuzzySearch';


export default function GlobalSearch({ onCloseDrawer }) {
    const {
        selectOptions,
        valueSelected,
        params
    } = useContext(FiltersContext);
    const [open, setOpen] = useState(false);
    const [inputValue, setInputValue] = useState('');
    // Track when the search bar width is transitioning so we can delay showing suggestions
    const [isAnimating, setIsAnimating] = useState(false);
    const containerRef = useRef(null);
    const prevHasTextRef = useRef(false);
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const flatIndicators = useFlatIndicators();

    const handleSelect = (event, newValue) => {
        console.log('Search selected:', newValue);
        
        // Check if an area (country) is selected
        if (!valueSelected.area?.id) {
            console.log('No area selected, redirecting to homepage');
            // If no area selected, redirect to homepage for area selection
            navigate('/');
            return;
        }

        // Find the section data to get the full structure
        const sectionData = selectOptions.domain_code?.find(
            section => section.value === newValue.sectionValue
        );
        
        console.log('Found section data:', sectionData);
        
        if (sectionData) {
            // Build the URL directly and navigate to the specific chart
            const years = params.year.join(",");
            const domainName = newValue.sectionValue.toLowerCase();
            const subCatParam = newValue.indicatorSlug;
            const areaId = valueSelected.area.id;
            const chartId = newValue.id;

            const pathSegments = [
                'indicators',
                domainName,
                subCatParam,
                encodeURIComponent(areaId),
                encodeURIComponent(years),
            ];
            if (chartId != null) pathSegments.push(encodeURIComponent(chartId));

            navigate(`/${pathSegments.join('/')}`);
        } else {
            console.error('Section data not found for:', newValue.sectionValue);
        }

        setOpen(false);
        setInputValue(''); // Clear input after selection
        isMobile && onCloseDrawer();
    };

    // Smooth scroll helper no longer needed here; indicator view handles initial scroll

    const handleInputChange = (event, newInputValue) => {
        const prevHasText = prevHasTextRef.current;
        const hasText = newInputValue.length > 0;
        const el = containerRef.current;
        const width = el ? el.offsetWidth : 0;
        // Heuristic: if user starts typing while the bar is in its contracted width, a width transition will start
        const SMALL_WIDTH_PX = 200; // contracted ~150px; wide is 20vw
        const willExpand = !prevHasText && hasText && !isMobile && width > 0 && width <= SMALL_WIDTH_PX;

        if (willExpand) {
            setIsAnimating(true);
        }

        setInputValue(newInputValue);
        if (hasText) {
            setOpen(true);
        } else {
            setOpen(false);
        }

        prevHasTextRef.current = hasText;
    };

    // Handlers to detect width transition on the search container
    const handleTransitionEnd = useCallback((e) => {
        if (e.propertyName === 'width') {
            // Defer to next frame to avoid a race with popup measurements
            requestAnimationFrame(() => setIsAnimating(false));
        }
    }, []);

    // No trailing second '#' support needed; we use standard ?scroll in the hash query

    return (
        <Box
            // Move width transition to the container so we can reliably listen to it
            onTransitionEnd={handleTransitionEnd}
            ref={containerRef}
            sx={{
                width: { xs: '100%', md: inputValue ? '20vw' : '150px' },
                mr: '10px',
                transition: 'width 0.4s ease',
                '&:hover': {
                    width: { xs: '100%', md: '20vw' },
                },
                // Keep background aligned with the input
                backgroundColor: theme.palette.secondary.dark,
                borderRadius: 1,
            }}
        >
            <Autocomplete
                options={flatIndicators}
                getOptionLabel={(option) => option.label}
                value={null}
                inputValue={inputValue}
                freeSolo
                onChange={handleSelect}
                // Do not open suggestions while the bar is animating its width
                open={open && !isAnimating}
                onInputChange={handleInputChange}
                filterOptions={(options, { inputValue }) => {
                    if (!inputValue) return options;
                    return fuzzySearchRanked(inputValue, options, (option) => `${option.label || ''} ${option.outcome || ''} ${option.theme || ''}`);
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        placeholder="Search indicator..."
                        inputProps={{
                            ...params.inputProps,
                            autoComplete: "off",
                            autoCorrect: "off",
                            autoCapitalize: "off",
                            spellCheck: "false",
                        }}
                        sx={{
                            "& .MuiInputBase-input::placeholder": {
                                fontSize: "10px",
                                color: theme.palette.primary.fontColor,
                                opacity: "0.7",
                                fontStyle: "italic",
                            },
                            "@media (min-width: 1200px)": {
                                "& .MuiInputBase-input::placeholder": {
                                    fontSize: "10px",
                                },
                            },
                            "@media (min-width: 1366px)": {
                                "& .MuiInputBase-input::placeholder": {
                                    fontSize: "11px",
                                },
                            },
                            backgroundColor: theme.palette.secondary.dark,
                        }}
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {params.InputProps.endAdornment}
                                    <InputAdornment position="end">
                                        <SearchIcon />
                                    </InputAdornment>
                                </>
                            ),
                        }}
                    />
                )}
                // Autocomplete should just fill the container; container handles width animation
                sx={{ width: '100%' }}
                renderOption={(props, option) => {
                    const { key, ...otherProps } = props;
                    return (
                        <Box
                            {...otherProps}
                            component="li"
                            style={{ display: "flex", alignItems: "flex-start" }}
                            key={key}
                        >
                            <Box sx={{ color: theme.palette.primary.main }}>
                                &#9679;&nbsp;&nbsp;
                            </Box>
                            {option.label}
                        </Box>
                    );
                }}
                isOptionEqualToValue={(option, value) =>
                    option.value.id === value?.value.id
                }
            />
        </Box>
    );
};


